import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PaymentDueAccountVM } from "@propertelligent/client-api/models/PaymentDueAccountVM";
import "./payment.css";
import H3 from "../typography/H3";
import EmptyState from "../emptystate";
import PaymentIcon from "../../assets/Payment-Icon.svg";
import SingleDropdown from "../singleDropdown/SingleDropdown";
import Chevren_downIcon from "../../assets/svgs/Chevren_down";
import { DateFilterTypesEnum, PaymentService } from "@propertelligent/client-api";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import apiHelper from "../../utils/apiHelper";
import DateHelper from "../../utils/DateHelper";
import SimpleSpinner from "../../components/loader";
import PaymentAlert from "./PaymentAlert";
import dateicon from "../../assets/Date.svg";
import docicon from "../../assets/Doc.svg";
import { formatWithCommas } from "../../utils/helper";
import UtilHelper from "../../utils/UtilHelper";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";

interface PaymentState {
  mode: 'full' | 'partial' | null; // Added null
  partialAmount: number;
}

interface WaivePaymentListProps {
  tenantId: number;
  setIsModalOpen: (value) => void;
  fetchData: any;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
} as const;

const RECEIVABLE_FILTER = "paidFlowTypeId == 2";

export default function WaivePaymentList({ tenantId, setIsModalOpen, fetchData, onSubmittingChange }: WaivePaymentListProps) {
  // State management
  const [paymentTenantlist, setPaymentTenantlist] = useState<PaymentDueAccountVM[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<DateFilterTypesEnum | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<PaymentDueAccountVM[]>([]);
  const [paymentStates, setPaymentStates] = useState<Record<number, PaymentState>>({});
  const [totalWaivedAmount, setTotalWaivedAmount] = useState(0);
  const [inputErrors, setInputErrors] = useState<Record<number, boolean>>({});
  const currencySymbol = useCurrencySymbol();

  const paymentsRef = useRef(paymentTenantlist);
  paymentsRef.current = paymentTenantlist;

  // Calculate total waived amount
  useEffect(() => {
    const total = selectedPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    setTotalWaivedAmount(total);
  }, [selectedPayments]);

  // Fetch payment data
  const fetchPaymentTenantlist = useCallback(async () => {
    setIsLoading(true);
    try {
      const isDateFiltered = dateFilterType !== null;

      const response = await apiHelper(
        PaymentService.getApiPaymentDueAccountTenantlist,
        { showNotification: false },
        tenantId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        RECEIVABLE_FILTER,
        undefined,
        isDateFiltered ? dateFilterType : undefined,
        isDateFiltered ? DateHelper.getClientDate() : undefined,
        undefined,
        undefined,
        isDateFiltered ? "DueDate" : undefined
      );

      setPaymentTenantlist(response?.data?.success || []);
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load receivable payments",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [dateFilterType, tenantId]);

  // Fetch data on mount and when filter changes
  useEffect(() => {
    fetchPaymentTenantlist();
  }, [fetchPaymentTenantlist]);

  // Initialize payment states when data loads - CHANGED to null
  useEffect(() => {
    if (paymentTenantlist.length === 0) return;

    const newStates = paymentTenantlist.reduce((acc, payment) => {
      acc[payment.id] = {
        mode: null, // Changed from 'full' to null
        partialAmount: payment.amount || 0
      };
      return acc;
    }, {} as Record<number, PaymentState>);

    setPaymentStates(newStates);
    setInputErrors({});
  }, [paymentTenantlist]);

  // Update select all state
  useEffect(() => {
    const allSelected = selectedPayments.length === paymentTenantlist.length && paymentTenantlist.length > 0;
    if (selectAll !== allSelected) {
      setSelectAll(allSelected);
    }
  }, [selectedPayments, paymentTenantlist, selectAll]);

  // Create selected payment IDs set for quick lookup
  const selectedPaymentIds = useMemo(
    () => new Set(selectedPayments.map(payment => payment.id)),
    [selectedPayments]
  );

  // Filter options
  const MENU_ITEMS = useMemo(() => [
    {
      label: "All Payments",
      action: () => setDateFilterType(null),
    },
    {
      label: "Next 30 Days",
      action: () => setDateFilterType(DATE_FILTERS.NEXT_30_DAYS),
    },
    {
      label: "Next 3 months",
      action: () => setDateFilterType(DATE_FILTERS.NEXT_3_MONTHS),
    },
    {
      label: "Next 6 months",
      action: () => setDateFilterType(DATE_FILTERS.NEXT_6_MONTHS),
    },
  ], []);

  // Validate partial amount
  const validatePartialAmount = useCallback((paymentId: number, amount: number) => {
    const payment = paymentsRef.current.find(p => p.id === paymentId);
    if (!payment) return false;

    return amount > 0 && amount <= (payment.amount || 0);
  }, []);

  // Payment selection handlers
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedPayments([]);
      // Reset all modes to null when deselecting all
      setPaymentStates(prev => {
        const newStates = { ...prev };
        Object.keys(newStates).forEach(id => {
          newStates[id] = {
            mode: null,
            partialAmount: newStates[id].partialAmount
          };
        });
        return newStates;
      });
      return;
    }

    const allValid = paymentTenantlist.every(payment => {
      if (paymentStates[payment.id]?.mode === 'partial') {
        return validatePartialAmount(payment.id, paymentStates[payment.id].partialAmount);
      }
      return true;
    });

    if (!allValid) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Cannot select all - some partial amounts are invalid",
        type: NotificationType.Warning,
      });
      return;
    }

    // Set all to 'full' mode when selecting all
    const newStates = { ...paymentStates };
    paymentTenantlist.forEach(payment => {
      if (!newStates[payment.id] || newStates[payment.id].mode === null) {
        newStates[payment.id] = {
          mode: 'full',
          partialAmount: payment.amount || 0
        };
      }
    });
    setPaymentStates(newStates);

    const allPayments = paymentTenantlist.map(payment => ({
      ...payment,
      amount: newStates[payment.id]?.mode === 'partial'
        ? newStates[payment.id].partialAmount
        : payment.amount
    }));
    setSelectedPayments(allPayments);
  }, [selectAll, paymentTenantlist, paymentStates, validatePartialAmount]);

  const handlePaymentSelect = useCallback((payment: PaymentDueAccountVM) => {
    const isCurrentlySelected = selectedPaymentIds.has(payment.id);

    if (isCurrentlySelected) {
      // Deselecting - remove from selected and reset mode to null
      setSelectedPayments(prev => prev.filter(p => p.id !== payment.id));
      setPaymentStates(prev => ({
        ...prev,
        [payment.id]: {
          mode: null,
          partialAmount: payment.amount || 0
        }
      }));
      return;
    }

    // Selecting - validate if partial mode
    if (paymentStates[payment.id]?.mode === 'partial') {
      const isValid = validatePartialAmount(payment.id, paymentStates[payment.id].partialAmount);
      if (!isValid) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Cannot select payment with invalid partial amount",
          type: NotificationType.Warning,
        });
        return;
      }
    }

    // Set mode to 'full' when selecting
    setPaymentStates(prev => ({
      ...prev,
      [payment.id]: {
        mode: 'full',
        partialAmount: payment.amount || 0
      }
    }));

    setSelectedPayments(prev => [
      ...prev,
      {
        ...payment,
        amount: payment.amount
      }
    ]);
  }, [paymentStates, validatePartialAmount, selectedPaymentIds]);

  // Payment mode handlers
  const handlePaymentModeChange = useCallback((paymentId: number, mode: 'full' | 'partial') => {
    const payment = paymentsRef.current.find(p => p.id === paymentId);
    if (!payment) return;

    setPaymentStates(prev => ({
      ...prev,
      [paymentId]: {
        mode,
        partialAmount: mode === 'partial'
          ? (prev[paymentId]?.partialAmount || payment.amount || 0)
          : payment.amount || 0
      }
    }));

    setInputErrors(prev => ({ ...prev, [paymentId]: false }));

    // Update selected payments if this payment is already selected
    if (selectedPaymentIds.has(paymentId)) {
      setSelectedPayments(prev => prev.map(p => {
        if (p.id === paymentId) {
          return {
            ...p,
            amount: mode === 'partial'
              ? (paymentStates[paymentId]?.partialAmount || payment.amount || 0)
              : payment.amount || 0
          };
        }
        return p;
      }));
    }
  }, [selectedPaymentIds, paymentStates]);

  const handlePartialAmountChange = useCallback((paymentId: number, value: string) => {
    const amount = parseFloat(value) || 0;
    const payment = paymentsRef.current.find(p => p.id === paymentId);

    if (!payment) return;

    const isValid = validatePartialAmount(paymentId, amount);

    setPaymentStates(prev => ({
      ...prev,
      [paymentId]: {
        ...prev[paymentId],
        partialAmount: amount
      }
    }));

    setInputErrors(prev => ({
      ...prev,
      [paymentId]: !isValid
    }));

    // Update selected payments if this payment is already selected
    if (selectedPaymentIds.has(paymentId)) {
      setSelectedPayments(prev => prev.map(p => {
        if (p.id === paymentId) {
          return {
            ...p,
            amount: amount
          };
        }
        return p;
      }));
    }
  }, [validatePartialAmount, selectedPaymentIds]);

  // Render payment items
  const paymentItems = useMemo(() => {
    return paymentTenantlist.map((payment, index) => {
      const isChecked = selectedPaymentIds.has(payment.id);
      const paymentState = paymentStates[payment.id] || { mode: null, partialAmount: payment.amount || 0 };
      const hasError = inputErrors[payment.id] || false;

      return (
        <div key={`payment-${payment.id}-${index}`}>
          <div className="payment-wrapper" style={{ alignItems: 'stretch' }}>
            <div className="d-block flex-grow-1 me-3">
              <div className="d-flex gap-2 align-items-center mb-2">
                <input
                  className="payment-checkbox"
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handlePaymentSelect(payment)}
                  aria-labelledby={`label-${payment.id}`}
                  disabled={paymentState.mode === 'partial' && hasError}
                />
                <h2 id={`label-${payment.id}`} className="address-title">
                  {payment.streetAddress}
                </h2>
              </div>

              <div className="info-line">
                <img
                  src={docicon}
                  alt=""
                  width={20}
                  height={20}
                  className="info-line-icon"
                  loading="lazy"
                />
                <span className="payment-desc">
                  {payment.description}
                </span>
              </div>

              <div className="info-line">
                <img
                  src={dateicon}
                  alt=""
                  width={20}
                  height={20}
                  className="info-line-icon"
                  loading="lazy"
                />
                <span className="due-text">
                  Due: {payment?.formatted?.DueDateDateOnly}
                </span>
              </div>
            </div>

            <div className="d-flex flex-column justify-content-between align-items-end" style={{ minWidth: 'fit-content' }}>
              <div className="d-flex gap-2">
                <div className="form-check form-check-inline m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`radio-${payment.id}`}
                    id={`full-${payment.id}`}
                    checked={isChecked && paymentState.mode === 'full'} // Only checked if checkbox is selected
                    onChange={() => handlePaymentModeChange(payment.id, 'full')}
                    disabled={!isChecked} // Disabled when checkbox is not checked
                  />
                  <label className="form-check-label" htmlFor={`full-${payment.id}`}>
                    Full
                  </label>
                </div>
                <div className="form-check form-check-inline m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`radio-${payment.id}`}
                    id={`partial-${payment.id}`}
                    checked={isChecked && paymentState.mode === 'partial'} // Only checked if checkbox is selected
                    onChange={() => handlePaymentModeChange(payment.id, 'partial')}
                    disabled={!isChecked} // Disabled when checkbox is not checked
                  />
                  <label className="form-check-label" htmlFor={`partial-${payment.id}`}>
                    Partial
                  </label>
                </div>
              </div>

              <div className="amount-display pt-2">
                {isChecked && paymentState.mode === 'full' ? (
                  <div className="amount-text" style={{ top: 0, right: 0 }}>
                    <CurrencyTooltip
                      content={payment?.formatted?.AmountRaw}
                      formatted={payment?.formatted?.Amount}
                    >
                      <span>{payment?.formatted?.Amount}</span>
                    </CurrencyTooltip>
                  </div>
                ) : isChecked && paymentState.mode === 'partial' ? (
                  <div className="partial-input-group">
                    <span className="partial-input-group-text">{currencySymbol}</span>
                    <input
                      type="number"
                      className={`form-control partial-amount-input ${hasError ? 'is-invalid' : ''}`}
                      value={paymentState.partialAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                          handlePartialAmountChange(payment.id, e.target.value);
                        }
                      }}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      disabled={!isChecked} // extra safety
                    />
                  </div>
                ) : (
                  // Unselected: same theme-aware amount color as selected (--Grey-Heading via .amount-text).
                  // Bootstrap text-muted used to override that token and be unreadable in dark mode here;
                  // .text-muted is now globally theme-aware (bug/ui-bugfix-batch2, see design-system.md),
                  // so this omission is no longer load-bearing -- kept as-is to avoid unrelated churn.
                  <div className="amount-text" style={{ top: 0, right: 0 }}>
                    <CurrencyTooltip
                      content={payment?.formatted?.AmountRaw}
                      formatted={payment?.formatted?.Amount}
                    >
                      <span>{payment?.formatted?.Amount}</span>
                    </CurrencyTooltip>
                  </div>
                )}
              </div>
            </div>
          </div>

          {paymentState.mode === 'partial' && hasError && (
            <PaymentAlert
              tone="error"
              message="Partial amount can't be greater than the actual amount"
            />
          )}

          {index < paymentTenantlist.length - 1 && <div className="separator-line"></div>}
        </div>
      );
    });
  }, [paymentTenantlist, selectedPaymentIds, paymentStates, inputErrors, handlePaymentSelect, handlePaymentModeChange, handlePartialAmountChange]);

  const activeFilterLabel = dateFilterType ? MENU_ITEMS.find(item => item.action.toString().includes(dateFilterType.toString()))?.label : "All Payments";

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {

    e.preventDefault();

    if (selectedPayments.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one Waive payment",
        type: NotificationType.Warning,
      });
      return;
    }

    // Check for any invalid partial amounts
    const hasInvalidPartial = selectedPayments.some(payment => {
      const state = paymentStates[payment.id];
      return state?.mode === 'partial' &&
        !validatePartialAmount(payment.id, state.partialAmount);
    });

    if (hasInvalidPartial) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Cannot submit - some partial amounts are invalid",
        type: NotificationType.Warning,
      });
      return;
    }
    
    onSubmittingChange?.(true);
    try {
      const paymentPromises = selectedPayments.map((item) => {
        return apiHelper(
          PaymentService.putApiPaymentDueAccountWaive,
          {
            successMessage: "Payment Waived",
            failureMessage: "Unable to Waive payment",
            showNotification: true,
          },
          {
            payDueAccountId: item?.id,
            amount: item?.amount,
          }
        );
      });

      await Promise.all(paymentPromises);
      setIsModalOpen(false);
      if (typeof fetchData === "function") {
        fetchData();
      }
    } catch (error: any) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: error?.message || "Something went wrong.",
        type: NotificationType.Warning,
      });
    } finally {
      onSubmittingChange?.(false);
    }
  };

  return (
    <>
      <div className="header-section d-flex align-items-center justify-space-between pb-2 pt-3 m-0">
        <H3 text={`${paymentTenantlist.length} Payments`} />
        <SingleDropdown
          triggerText={activeFilterLabel || "Filter"}
          triggerIcon={<Chevren_downIcon width={"18"} height={"18"} />}
          triggerIconPosition="right"
          menuItems={MENU_ITEMS}
        />
      </div>

      {isLoading ? (
        <div style={{ margin: '50% auto' }}>
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (paymentTenantlist.length > 0 ? (
        <form id="WaivePayment" onSubmit={onSubmit}>
          <div className="main-wrapper waive-payment-container">
            <div className="checkbox-wrapper">
              <input
                className="payment-checkbox"
                type="checkbox"
                id="selectAllCheckbox"
                checked={selectAll}
                onChange={handleSelectAll}
                disabled={paymentTenantlist.length === 0}
                aria-label={selectAll ? "Deselect all payments" : "Select all payments"}
              />
              <label className="checkbox-inline-label" htmlFor="selectAllCheckbox">
                Select All
              </label>
            </div>

            <div className="separator-line"></div>

            <div className="payments-list">
              {paymentItems}
            </div>

            <div className="payment-Waiving-row">
              <span className="payment-Waiving-label">{"You're Waiving"}</span>
              <div className="dotted-line"></div>
              <CurrencyTooltip
                content={UtilHelper.CurrencyFormat(totalWaivedAmount, false)}
                formatted={UtilHelper.formatCurrency(totalWaivedAmount, false)}
              >
                <span className="payment-amount">{UtilHelper.formatCurrency(totalWaivedAmount, false)}</span>
              </CurrencyTooltip>
            </div>
          </div>
        </form>
      ) : (
        <EmptyState
          icon={PaymentIcon}
          heading="No Receivable Payments Found"
          isButton={false}
          className="empty-state-wrapper"
        />
      ))}
    </>
  );
}